import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import IosInstallSheet from "@/components/IosInstallSheet";
import { useI18n } from "@/context/I18n";

const DISMISSED_KEY = "sonlyo.install.dismissed";

export function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/**
 * Shared install state.
 *
 * Chrome fires `beforeinstallprompt` when the site meets the install criteria;
 * holding on to that event is the only way to trigger the one tap install
 * later. iOS has no such API, so there the only route is the Share sheet and
 * all we can do is show where it is.
 */
export function useInstall() {
  // index.html captures the event before the bundle runs, because Chrome fires
  // it once and does not replay it for a listener that attaches later.
  const [deferred, setDeferred] = useState(() => window.__sonlyoInstall || null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onAvailable = () => setDeferred(window.__sonlyoInstall || null);
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    // Both the early capture and a late direct event are handled.
    window.addEventListener("sonlyo:installable", onAvailable);
    window.addEventListener("sonlyo:installed", onInstalled);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("beforeinstallprompt", onAvailable);
    onAvailable();
    return () => {
      window.removeEventListener("sonlyo:installable", onAvailable);
      window.removeEventListener("sonlyo:installed", onInstalled);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("beforeinstallprompt", onAvailable);
    };
  }, []);

  const install = async () => {
    const event = deferred || window.__sonlyoInstall;
    if (!event) return "unavailable";
    try {
      // A stored prompt goes stale once Chrome has used or expired it, and
      // then this call quietly does nothing. Report that instead, so the
      // caller can fall back to showing where the browser menu is.
      const shown = event.prompt();
      const choice = await Promise.race([
        event.userChoice,
        new Promise((resolve) => setTimeout(() => resolve({ outcome: "timeout" }), 2500)),
      ]);
      await shown;
      window.__sonlyoInstall = null;
      setDeferred(null);
      return choice && choice.outcome === "timeout" ? "unavailable" : choice && choice.outcome;
    } catch {
      window.__sonlyoInstall = null;
      setDeferred(null);
      return "unavailable";
    }
  };

  return {
    install,
    installed,
    canInstall: !!(deferred || window.__sonlyoInstall),
    needsIosSteps: isIos() && !installed,
  };
}

/**
 * Banner shown once on a phone, so installing is one tap from wherever the
 * listener happens to be rather than something to go hunting for in Settings.
 */
export default function InstallPrompt() {
  const { t } = useI18n();
  const { install, installed, canInstall, needsIosSteps } = useInstall();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [showIos, setShowIos] = useState(false);

  const close = () => {
    setDismissed(true);
    setShowIos(false);
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      /* private mode */
    }
  };

  if (installed || dismissed) return null;
  // Even without a prompt to fire, the browser menu can install, so the
  // banner still has somewhere useful to send a phone user.
  const onPhone = window.matchMedia("(max-width: 767px)").matches;
  if (!canInstall && !needsIosSteps && !onPhone) return null;

  return (
    <div className="fixed bottom-[146px] left-3 right-3 z-50 md:hidden" data-testid="install-banner">
      <div className="bg-[#141414] border border-white/10 rounded-2xl shadow-2xl p-3">
        <div className="flex items-center gap-3">
          <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm leading-tight">{t("install_title")}</p>
            <p className="text-xs text-zinc-400 leading-tight mt-0.5">{t("install_banner_sub")}</p>
          </div>
          <button
            data-testid="install-banner-btn"
            onClick={async () => {
              const result = canInstall ? await install() : "unavailable";
              if (result === "unavailable") setShowIos(true);
            }}
            className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1.5 shrink-0"
          >
            <Download className="w-4 h-4" /> {t("install_add")}
          </button>
          <button onClick={close} aria-label="Close" className="text-zinc-500 hover:text-white p-1 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

      </div>
      {showIos && <IosInstallSheet onClose={() => setShowIos(false)} />}
    </div>
  );
}
